import { useState, useMemo, Fragment } from 'react';
import { Button, Spinner, Input, useDisclosure, Chip } from '@heroui/react';
import {
  Plus, ChevronRight, ChevronDown, Layers, FolderOpen, MapPin,
  Pencil, Trash2, Copy, GripVertical, DollarSign, Package, FileDown,
  SwatchBook, Wrench, AlertTriangle, Settings2, HardHat, Ban, Scissors,
} from 'lucide-react';
import { toast } from 'sonner';
import { useProjectPhases, useCreateProjectPhase, useUpdateProjectPhase, useDeleteProjectPhase } from '@/api/project-phases';
import { useAreaGroups, useCreateAreaGroup, useUpdateAreaGroup, useDeleteAreaGroup } from '@/api/area-groups';
import { useAreas, useUpdateArea } from '@/api/areas';
import { useLineItems, useDeleteLineItem, useDuplicateLineItem, useUpdateLineItem, useBulkUpdateLineItems } from '@/api/line-items';
import { useProject, useUpdateProject } from '@/api/projects';
import { LaborCategory } from '@estimator/shared';
import { useCustomCharges, useCreateCustomCharge, useUpdateCustomCharge, useDeleteCustomCharge } from '@/api/custom-charges';
import { useProjectMaterials } from '@/api/project-materials';
import { useProjectCost } from '@/api/calculations';
import { useDownloadEstimatePdf } from '@/api/pdf-generator';
import { useRunNesting } from '@/api/nesting';
import AddLineItemDialog from './AddLineItemDialog';
import BanquetteBaseModal from './BanquetteBaseModal';
import BackCushionModal from './BackCushionModal';
import SeatCushionModal from './SeatCushionModal';
import ArmModal from './ArmModal';
import MetalBaseModal from './MetalBaseModal';
import LineItemModal from './LineItemModal';
import ProjectMaterialsModal from './ProjectMaterialsModal';
import AreaMaterialsModal from './AreaMaterialsModal';
import ProductPickerModal from './ProductPickerModal';
import ProjectCostSummary from './ProjectCostSummary';
import InlineEditableCell from './InlineEditableCell';
import ProjectTotalsBar from './ProjectTotalsBar';
import CostBreakdownPanel from './CostBreakdownPanel';
import MaterialsPopup from './MaterialsPopup';
import HardwarePopup from './HardwarePopup';
import type { Area, LineItem, CustomCharge } from '@estimator/shared';

interface EstimateTabProps {
  projectId: string;
}

export default function EstimateTab({ projectId }: EstimateTabProps) {
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);
  const [expandedPhases, setExpandedPhases] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [editingLineItemId, setEditingLineItemId] = useState<string | null>(null);
  const [editingBanquetteId, setEditingBanquetteId] = useState<string | null>(null);
  const [editingBanquetteConfig, setEditingBanquetteConfig] = useState<string | undefined>(undefined);
  const [editingBanquetteDims, setEditingBanquetteDims] = useState<{ width: number; height: number; depth: number } | undefined>(undefined);
  const [editingBackCushionId, setEditingBackCushionId] = useState<string | null>(null);
  const [editingBackCushionConfig, setEditingBackCushionConfig] = useState<string | undefined>(undefined);
  const [editingBackCushionDims, setEditingBackCushionDims] = useState<{ width: number; height: number; depth: number } | undefined>(undefined);
  const [editingSeatCushionId, setEditingSeatCushionId] = useState<string | null>(null);
  const [editingSeatCushionConfig, setEditingSeatCushionConfig] = useState<string | undefined>(undefined);
  const [editingSeatCushionDims, setEditingSeatCushionDims] = useState<{ width: number; height: number; depth: number } | undefined>(undefined);
  const [editingArmId, setEditingArmId] = useState<string | null>(null);
  const [editingArmConfig, setEditingArmConfig] = useState<string | undefined>(undefined);
  const [editingArmDims, setEditingArmDims] = useState<{ width: number; height: number; depth: number } | undefined>(undefined);
  const [editingMetalBaseId, setEditingMetalBaseId] = useState<string | null>(null);
  const [editingMetalBaseConfig, setEditingMetalBaseConfig] = useState<string | undefined>(undefined);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [costBreakdownItemId, setCostBreakdownItemId] = useState<string | null>(null);
  const [materialsPopupItemId, setMaterialsPopupItemId] = useState<string | null>(null);
  const [hardwarePopupItemId, setHardwarePopupItemId] = useState<string | null>(null);

  // Line item modal
  const lineItemModal = useDisclosure();
  // Add line item type selection dialog
  const addLineItemDialog = useDisclosure();
  // Banquette base modal
  const banquetteModal = useDisclosure();
  // Back cushion modal
  const backCushionModal = useDisclosure();
  // Seat cushion modal
  const seatCushionModal = useDisclosure();
  const armModal = useDisclosure();
  const metalBaseModal = useDisclosure();
  // Project materials modal
  const projectMaterialsModal = useDisclosure();
  // Area materials modal
  const areaMaterialsModal = useDisclosure();
  // Product picker modal
  const productPickerModal = useDisclosure();

  // Data fetching
  const { data: phases = [], isLoading: loadingPhases } = useProjectPhases(projectId);
  const { data: groups = [], isLoading: loadingGroups } = useAreaGroups(projectId);
  const { data: areas = [], isLoading: loadingAreas } = useAreas(projectId);
  const { data: lineItems = [], isLoading: loadingItems } = useLineItems(selectedAreaId ?? undefined);
  const { data: customCharges = [] } = useCustomCharges(selectedAreaId ?? undefined);
  const { data: projectCost } = useProjectCost(projectId);

  // Mutations
  const createPhase = useCreateProjectPhase();
  const updatePhase = useUpdateProjectPhase();
  const deletePhase = useDeleteProjectPhase();
  const createGroup = useCreateAreaGroup();
  const deleteGroup = useDeleteAreaGroup();
  const updateArea = useUpdateArea();
  const deleteLineItem = useDeleteLineItem();
  const duplicateLineItem = useDuplicateLineItem();
  const createCharge = useCreateCustomCharge();
  const updateCharge = useUpdateCustomCharge();
  const deleteCharge = useDeleteCustomCharge();
  const downloadPdf = useDownloadEstimatePdf();
  const runNesting = useRunNesting();
  const updateLineItem = useUpdateLineItem();
  const bulkUpdateLineItems = useBulkUpdateLineItems();
  const { data: project } = useProject(projectId);
  const updateProject = useUpdateProject();
  const [laborSettingsOpen, setLaborSettingsOpen] = useState(false);

  const selectedArea = areas.find((a: Area) => a.id === selectedAreaId);

  // Build tree structure
  const tree = useMemo(() => {
    // Areas by group
    const areasByGroup = new Map<string | null, Area[]>();
    // Areas by phase (ungrouped)
    const areasByPhase = new Map<string | null, Area[]>();

    for (const area of areas as Area[]) {
      if (area.area_group_id) {
        const list = areasByGroup.get(area.area_group_id) || [];
        list.push(area);
        areasByGroup.set(area.area_group_id, list);
      } else if (area.phase_id) {
        const list = areasByPhase.get(area.phase_id) || [];
        list.push(area);
        areasByPhase.set(area.phase_id, list);
      }
    }

    const ungroupedAreas = (areas as Area[]).filter((a) => !a.area_group_id && !a.phase_id);

    return { areasByGroup, areasByPhase, ungroupedAreas };
  }, [areas]);

  const lineItemsWithCost = useMemo(() => {
    const areaCost = projectCost?.areas.find((a) => a.area_id === selectedAreaId);
    return (lineItems as LineItem[]).map((item) => {
      const cost = areaCost?.line_items.find((li) => li.item_id === item.id);
      const labor = projectCost?.labor_per_item?.[item.id] ?? 0;
      return {
        ...item,
        _materialCost: cost?.total_material_cost ?? null,
        _materialCostSource: cost?.material_cost_source ?? null,
        _hardwareCost: cost?.hardware_cost ?? null,
        _finishCost: cost?.finish_cost ?? null,
        _laborCost: labor || null,
        _totalCost: cost ? ((cost.total_material_cost ?? 0) + cost.hardware_cost + cost.finish_cost + labor) : null,
        _price: (cost as any)?.price ?? null,
        _deferred: cost?.calculation_deferred ?? false,
        _fabricDetails: (cost?.material_details ?? []).filter(
          (d) => d.yards != null && d.yards > 0,
        ),
      };
    });
  }, [lineItems, projectCost, selectedAreaId]);

  // Toggle phase expansion
  const togglePhase = (phaseId: string) => {
    setExpandedPhases((prev) => {
      const next = new Set(prev);
      next.has(phaseId) ? next.delete(phaseId) : next.add(phaseId);
      return next;
    });
  };

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.has(groupId) ? next.delete(groupId) : next.add(groupId);
      return next;
    });
  };

  // Handlers
  const handleAddPhase = async () => {
    const name = prompt('Phase name:');
    if (!name?.trim()) return;
    try {
      await createPhase.mutateAsync({ project_id: projectId, name: name.trim(), sort_order: phases.length });
      toast.success('Phase created');
    } catch { toast.error('Failed to create phase'); }
  };

  const handleAddGroup = async (phaseId?: string) => {
    const name = prompt('Group name:');
    if (!name?.trim()) return;
    try {
      await createGroup.mutateAsync({
        project_id: projectId,
        name: name.trim(),
        phase_id: phaseId ?? null,
        sort_order: groups.length,
      });
      toast.success('Group created');
    } catch { toast.error('Failed to create group'); }
  };

  const handleDeletePhase = async (id: string, name: string) => {
    if (!confirm(`Delete phase "${name}"? Areas will be unlinked.`)) return;
    try {
      await deletePhase.mutateAsync(id);
      toast.success('Phase deleted');
    } catch { toast.error('Failed to delete phase'); }
  };

  const handleDeleteGroup = async (id: string, name: string) => {
    if (!confirm(`Delete group "${name}"? Areas will be unlinked.`)) return;
    try {
      await deleteGroup.mutateAsync(id);
      toast.success('Group deleted');
    } catch { toast.error('Failed to delete group'); }
  };

  const handleOpenLineItem = (itemId?: string) => {
    if (itemId) {
      const item = lineItemsWithCost?.find((li: any) => li.id === itemId);
      // Check if this is a back cushion item
      if (item?.back_cushion_config && item.back_cushion_config !== '{}') {
        setEditingBackCushionId(itemId);
        setEditingBackCushionConfig(item.back_cushion_config);
        setEditingBackCushionDims({ width: item.width ?? 0, height: item.height ?? 0, depth: item.depth ?? 0 });
        backCushionModal.onOpen();
        return;
      }
      // Check if this is a seat cushion item
      const seatCfg = (item as any)?.seat_cushion_config;
      if (item && seatCfg && seatCfg !== '{}') {
        setEditingSeatCushionId(itemId);
        setEditingSeatCushionConfig(seatCfg);
        setEditingSeatCushionDims({ width: item.width ?? 0, height: item.height ?? 0, depth: item.depth ?? 0 });
        seatCushionModal.onOpen();
        return;
      }
      // Check if this is an arm item
      const armCfg = (item as any)?.arm_config;
      if (item && armCfg && armCfg !== '{}') {
        setEditingArmId(itemId);
        setEditingArmConfig(armCfg);
        setEditingArmDims({ width: item.width ?? 0, height: item.height ?? 0, depth: item.depth ?? 0 });
        armModal.onOpen();
        return;
      }
      // Check if this is a metal base item
      const metalCfg = (item as any)?.metal_base_config;
      if (item && metalCfg && metalCfg !== '{}') {
        setEditingMetalBaseId(itemId);
        setEditingMetalBaseConfig(metalCfg);
        metalBaseModal.onOpen();
        return;
      }
      // Check if this is a banquette item
      if (item?.banquette_config && item.banquette_config !== '{}') {
        setEditingBanquetteId(itemId);
        setEditingBanquetteConfig(item.banquette_config);
        setEditingBanquetteDims({ width: item.width ?? 0, height: item.height ?? 0, depth: item.depth ?? 0 });
        banquetteModal.onOpen();
        return;
      }
    }
    setEditingLineItemId(itemId ?? null);
    lineItemModal.onOpen();
  };

  const handleSelectCustom = () => {
    setEditingLineItemId(null);
    lineItemModal.onOpen();
  };

  const handleSelectFromLibrary = () => {
    productPickerModal.onOpen();
  };

  const handleSelectBanquette = () => {
    banquetteModal.onOpen();
  };

  const handleSelectBackCushion = () => {
    backCushionModal.onOpen();
  };

  const handleSelectSeatCushion = () => {
    seatCushionModal.onOpen();
  };

  const handleSelectArm = () => {
    armModal.onOpen();
  };

  const handleSelectMetalBase = () => {
    metalBaseModal.onOpen();
  };

  const handleDeleteLineItem = async (id: string, desc: string) => {
    if (!confirm(`Delete "${desc}"?`)) return;
    try {
      await deleteLineItem.mutateAsync(id);
      toast.success('Line item deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleDuplicate = async (id: string) => {
    try {
      await duplicateLineItem.mutateAsync({ id });
      toast.success('Line item duplicated');
    } catch { toast.error('Failed to duplicate'); }
  };

  // ── Labor exclusion helpers ──
  const excludedCategories = useMemo(() => {
    const raw = (project as any)?.excluded_labor_categories;
    if (!raw) return new Set<string>();
    return new Set(raw.split(',').map((s: string) => s.trim()).filter(Boolean));
  }, [(project as any)?.excluded_labor_categories]);

  const handleToggleLaborCategory = async (category: string) => {
    const next = new Set(excludedCategories);
    if (next.has(category)) next.delete(category);
    else next.add(category);
    const value = next.size > 0 ? Array.from(next).join(',') : null;
    try {
      await updateProject.mutateAsync({ id: projectId, excluded_labor_categories: value });
    } catch { toast.error('Failed to update labor settings'); }
  };

  const handleBulkExcludeLabor = async (exclude: boolean) => {
    const ids = Array.from(selectedItemIds);
    if (!ids.length) return;
    try {
      await bulkUpdateLineItems.mutateAsync({ ids, updates: { exclude_labor: exclude } });
      toast.success(`${exclude ? 'Excluded' : 'Included'} labor for ${ids.length} item(s)`);
      setSelectedItemIds(new Set());
    } catch { toast.error('Failed to update items'); }
  };

  const handleRunNesting = async () => {
    try {
      await runNesting.mutateAsync(projectId);
      toast.success('Nesting optimization complete');
    } catch {
      toast.error('Failed to run nesting optimization');
    }
  };

  const handleToggleItemLabor = async (itemId: string, currentValue: boolean) => {
    try {
      await updateLineItem.mutateAsync({ id: itemId, exclude_labor: !currentValue });
    } catch { toast.error('Failed to update'); }
  };

  const handleAddCharge = async () => {
    if (!selectedAreaId) return;
    try {
      await createCharge.mutateAsync({
        area_id: selectedAreaId,
        description: 'New Charge',
        quantity: 1,
        unit_cost: 0,
        category: 'Miscellaneous',
      });
      toast.success('Custom charge added');
    } catch { toast.error('Failed to add charge'); }
  };

  const handleDeleteCharge = async (id: string) => {
    if (!confirm('Delete this custom charge?')) return;
    try {
      await deleteCharge.mutateAsync(id);
      toast.success('Charge deleted');
    } catch { toast.error('Failed to delete charge'); }
  };

  const handleInlineEdit = async (itemId: string, field: string, value: number) => {
    try {
      await updateLineItem.mutateAsync({ id: itemId, [field]: value });
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleSelectAll = () => {
    if (selectedItemIds.size === lineItems.length) {
      setSelectedItemIds(new Set());
    } else {
      setSelectedItemIds(new Set((lineItems as LineItem[]).map((i) => i.id)));
    }
  };

  const handleSelectItem = (itemId: string) => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      next.has(itemId) ? next.delete(itemId) : next.add(itemId);
      return next;
    });
  };

  const isLoading = loadingPhases || loadingGroups || loadingAreas;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  // Render an area item in the tree
  const renderAreaItem = (area: Area, indent: number = 0) => (
    <button
      key={area.id}
      onClick={() => setSelectedAreaId(area.id)}
      className="w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded transition-colors text-left"
      style={{
        paddingLeft: `${8 + indent * 16}px`,
        backgroundColor: selectedAreaId === area.id ? 'var(--bg-hover)' : 'transparent',
        color: selectedAreaId === area.id ? 'var(--accent)' : 'var(--text-secondary)',
      }}
    >
      <MapPin size={13} style={{ flexShrink: 0 }} />
      <span className="truncate">{area.name}</span>
    </button>
  );

  return (
    <div className="flex h-full" style={{ minHeight: 'calc(100vh - 180px)' }}>
      {/* ─── Left Sidebar: Project Tree ──────────────────────────── */}
      <div
        className="w-64 flex-shrink-0 border-r overflow-y-auto"
        style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-surface)' }}
      >
        {/* Sidebar header */}
        <div className="p-3 border-b flex items-center justify-between" style={{ borderColor: 'var(--border)' }}>
          <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
            Project Structure
          </span>
          <div className="flex gap-1">
            <button
              onClick={handleAddPhase}
              className="p-1 rounded hover:bg-[var(--bg-hover)] transition-colors"
              title="Add Phase"
            >
              <Plus size={14} style={{ color: 'var(--text-muted)' }} />
            </button>
          </div>
        </div>

        <div className="p-1">
          {/* Phases */}
          {phases.map((phase) => {
            const isExpanded = expandedPhases.has(phase.id);
            const phaseGroups = groups.filter((g) => g.phase_id === phase.id);
            const phaseAreas = tree.areasByPhase.get(phase.id) || [];

            return (
              <div key={phase.id}>
                <div className="flex items-center group">
                  <button
                    onClick={() => togglePhase(phase.id)}
                    className="flex-1 flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium rounded transition-colors"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                    <Layers size={13} style={{ color: 'var(--accent)' }} />
                    <span className="truncate">{phase.name}</span>
                  </button>
                  <div className="hidden group-hover:flex items-center gap-0.5 pr-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleAddGroup(phase.id); }}
                      className="p-0.5 rounded hover:bg-[var(--bg-hover)]"
                      title="Add group to phase"
                    >
                      <Plus size={12} style={{ color: 'var(--text-muted)' }} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeletePhase(phase.id, phase.name); }}
                      className="p-0.5 rounded hover:bg-[var(--bg-hover)]"
                      title="Delete phase"
                    >
                      <Trash2 size={12} style={{ color: 'var(--text-muted)' }} />
                    </button>
                  </div>
                </div>
                {isExpanded && (
                  <div>
                    {phaseGroups.map((group) => {
                      const groupExpanded = expandedGroups.has(group.id);
                      const groupAreas = tree.areasByGroup.get(group.id) || [];
                      return (
                        <div key={group.id}>
                          <div className="flex items-center group">
                            <button
                              onClick={() => toggleGroup(group.id)}
                              className="flex-1 flex items-center gap-1.5 px-2 py-1.5 text-xs rounded transition-colors"
                              style={{ paddingLeft: '24px', color: 'var(--text-secondary)' }}
                            >
                              {groupExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                              <FolderOpen size={12} />
                              <span className="truncate">{group.name}</span>
                            </button>
                            <button
                              onClick={() => handleDeleteGroup(group.id, group.name)}
                              className="hidden group-hover:block p-0.5 rounded hover:bg-[var(--bg-hover)] mr-1"
                              title="Delete group"
                            >
                              <Trash2 size={11} style={{ color: 'var(--text-muted)' }} />
                            </button>
                          </div>
                          {groupExpanded && groupAreas.map((a) => renderAreaItem(a, 3))}
                        </div>
                      );
                    })}
                    {phaseAreas.map((a) => renderAreaItem(a, 2))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Standalone groups (no phase) */}
          {groups
            .filter((g) => !g.phase_id)
            .map((group) => {
              const groupExpanded = expandedGroups.has(group.id);
              const groupAreas = tree.areasByGroup.get(group.id) || [];
              return (
                <div key={group.id}>
                  <div className="flex items-center group">
                    <button
                      onClick={() => toggleGroup(group.id)}
                      className="flex-1 flex items-center gap-1.5 px-2 py-1.5 text-xs rounded transition-colors"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      {groupExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                      <FolderOpen size={13} />
                      <span className="truncate">{group.name}</span>
                    </button>
                    <button
                      onClick={() => handleDeleteGroup(group.id, group.name)}
                      className="hidden group-hover:block p-0.5 rounded hover:bg-[var(--bg-hover)] mr-1"
                      title="Delete group"
                    >
                      <Trash2 size={11} style={{ color: 'var(--text-muted)' }} />
                    </button>
                  </div>
                  {groupExpanded && groupAreas.map((a) => renderAreaItem(a, 2))}
                </div>
              );
            })}

          {/* Ungrouped areas */}
          {tree.ungroupedAreas.length > 0 && (
            <div>
              <div
                className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider"
                style={{ color: 'var(--text-muted)' }}
              >
                Ungrouped
              </div>
              {tree.ungroupedAreas.map((a) => renderAreaItem(a, 1))}
            </div>
          )}

          {/* Add standalone group button */}
          <button
            onClick={() => handleAddGroup()}
            className="w-full flex items-center gap-1.5 px-2 py-1.5 mt-1 text-xs rounded transition-colors hover:bg-[var(--bg-hover)]"
            style={{ color: 'var(--text-muted)' }}
          >
            <Plus size={12} />
            Add Group
          </button>
        </div>
      </div>

      {/* ─── Right Panel: Area Detail + Line Items ──────────────── */}
      <div className="flex-1 overflow-y-auto" style={{ backgroundColor: 'var(--bg-base)' }}>
        {!selectedAreaId ? (
          <div className="flex flex-col items-center justify-center h-full gap-2" style={{ color: 'var(--text-muted)' }}>
            <Package size={40} strokeWidth={1} />
            <p className="text-sm">Select an area to view line items</p>
          </div>
        ) : loadingItems ? (
          <div className="flex items-center justify-center h-64">
            <Spinner size="lg" />
          </div>
        ) : (
          <div className="p-4 max-w-[1400px]">
            <ProjectTotalsBar projectCost={projectCost ?? null} isLoading={!projectCost} />

            {/* Area header */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                  {selectedArea?.name ?? 'Area'}
                </h2>
                {selectedArea?.notes && (
                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                    {selectedArea.notes}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                {/* Labor Settings Popover */}
                <div className="relative">
                  <Button
                    size="sm"
                    variant="bordered"
                    onPress={() => setLaborSettingsOpen(!laborSettingsOpen)}
                    startContent={<Settings2 size={14} />}
                    className="border-[var(--border)] text-[var(--text-secondary)]"
                  >
                    Labor Settings
                    {excludedCategories.size > 0 && (
                      <Chip size="sm" color="warning" variant="flat" className="ml-1 h-4 min-w-0 px-1">
                        {excludedCategories.size}
                      </Chip>
                    )}
                  </Button>
                  {laborSettingsOpen && (
                    <>
                      <div className="fixed inset-0 z-30" onClick={() => setLaborSettingsOpen(false)} />
                      <div
                        className="absolute right-0 top-full mt-1 z-40 rounded-lg border p-3 shadow-lg min-w-[220px]"
                        style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border)' }}
                      >
                        <p className="text-[10px] uppercase font-semibold mb-2" style={{ color: 'var(--text-muted)' }}>
                          Included Labor Categories
                        </p>
                        {Object.values(LaborCategory).map((cat) => (
                          <label
                            key={cat}
                            className="flex items-center gap-2 py-1 text-xs cursor-pointer hover:bg-[var(--bg-hover)] px-1 rounded"
                            style={{ color: 'var(--text-primary)' }}
                          >
                            <input
                              type="checkbox"
                              checked={!excludedCategories.has(cat)}
                              onChange={() => handleToggleLaborCategory(cat)}
                              className="accent-[var(--accent)]"
                            />
                            {cat}
                          </label>
                        ))}
                        <p className="text-[10px] mt-2" style={{ color: 'var(--text-muted)' }}>
                          Unchecked categories will not be applied to this project.
                        </p>
                      </div>
                    </>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="bordered"
                  onPress={areaMaterialsModal.onOpen}
                  className="border-[var(--border)] text-[var(--text-secondary)]"
                >
                  Area Materials
                </Button>
                <Button
                  size="sm"
                  variant="bordered"
                  onPress={projectMaterialsModal.onOpen}
                  className="border-[var(--border)] text-[var(--text-secondary)]"
                >
                  Project Materials
                </Button>
                <Button
                  size="sm"
                  variant="bordered"
                  isLoading={downloadPdf.isPending}
                  onPress={() => {
                    downloadPdf.mutate(projectId, {
                      onSuccess: () => toast.success('Estimate PDF downloaded'),
                      onError: () => toast.error('Failed to generate estimate PDF'),
                    });
                  }}
                  startContent={!downloadPdf.isPending ? <FileDown size={14} /> : undefined}
                  className="border-[var(--border)] text-[var(--text-secondary)]"
                >
                  Export PDF
                </Button>
                <Button
                  size="sm"
                  variant="flat"
                  isLoading={runNesting.isPending}
                  onPress={handleRunNesting}
                  startContent={!runNesting.isPending ? <Scissors size={14} /> : undefined}
                  className="border-[var(--border)] text-[var(--text-secondary)]"
                >
                  Run Nesting
                </Button>
                <Button
                  size="sm"
                  color="primary"
                  onPress={addLineItemDialog.onOpen}
                  startContent={<Plus size={14} />}
                >
                  Add Line Item
                </Button>
              </div>
            </div>

            {/* Nesting staleness banner */}
            {projectCost?.material_cost_source === 'pending' && (
              <div className="flex items-center gap-3 px-4 py-3 mb-4 rounded-lg" style={{ backgroundColor: 'rgba(230,126,34,0.1)', border: '1px solid rgba(230,126,34,0.3)' }}>
                <AlertTriangle size={16} style={{ color: '#e67e22', flexShrink: 0 }} />
                <span className="text-xs flex-1" style={{ color: '#e67e22' }}>
                  Material costs require nesting optimization. Run nesting to calculate accurate costs.
                </span>
                <Button
                  size="sm"
                  color="warning"
                  variant="flat"
                  isLoading={runNesting.isPending}
                  onPress={handleRunNesting}
                  startContent={!runNesting.isPending ? <Scissors size={14} /> : undefined}
                >
                  Run Nesting
                </Button>
              </div>
            )}

            {/* Bulk Actions Bar */}
            {selectedItemIds.size > 0 && (
              <div
                className="flex items-center gap-3 mb-3 px-3 py-2 rounded-lg border"
                style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-panel)' }}
              >
                <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
                  {selectedItemIds.size} selected
                </span>
                <Button
                  size="sm"
                  variant="flat"
                  startContent={<Ban size={13} />}
                  onPress={() => handleBulkExcludeLabor(true)}
                  isLoading={bulkUpdateLineItems.isPending}
                >
                  Exclude Labor
                </Button>
                <Button
                  size="sm"
                  variant="flat"
                  startContent={<HardHat size={13} />}
                  onPress={() => handleBulkExcludeLabor(false)}
                  isLoading={bulkUpdateLineItems.isPending}
                >
                  Include Labor
                </Button>
                <Button size="sm" variant="light" onPress={() => setSelectedItemIds(new Set())}>
                  Clear
                </Button>
              </div>
            )}

            {/* Line Items Table */}
            <div className="rounded-lg border overflow-hidden overflow-x-auto mb-6" style={{ borderColor: 'var(--border)' }}>
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ backgroundColor: 'var(--bg-panel)' }}>
                    <th className="px-2 py-2 text-center font-medium" style={{ color: 'var(--text-secondary)', width: '32px' }}>
                      <input
                        type="checkbox"
                        checked={selectedItemIds.size === lineItems.length && lineItems.length > 0}
                        onChange={handleSelectAll}
                        className="accent-[var(--accent)]"
                      />
                    </th>
                    <th className="px-3 py-2 text-left font-medium" style={{ color: 'var(--text-secondary)' }}>Code</th>
                    <th className="px-3 py-2 text-left font-medium min-w-[140px]" style={{ color: 'var(--text-secondary)' }}>Description</th>
                    <th className="px-2 py-2 text-center font-medium w-[50px]" style={{ color: 'var(--text-secondary)' }}>Qty</th>
                    <th className="px-2 py-2 text-center font-medium w-[50px]" style={{ color: 'var(--text-secondary)' }}>W</th>
                    <th className="px-2 py-2 text-center font-medium w-[50px]" style={{ color: 'var(--text-secondary)' }}>H</th>
                    <th className="px-2 py-2 text-center font-medium w-[50px]" style={{ color: 'var(--text-secondary)' }}>D</th>
                    <th className="px-2 py-2 text-center font-medium" style={{ color: 'var(--text-secondary)' }}>Doors</th>
                    <th className="px-2 py-2 text-center font-medium" style={{ color: 'var(--text-secondary)' }}>Dwrs</th>
                    <th className="px-2 py-2 text-center font-medium" style={{ color: 'var(--text-secondary)' }}>Shlvs</th>
                    <th className="px-2 py-2 text-right font-medium" style={{ color: 'var(--text-secondary)' }}>Mat $</th>
                    <th className="px-2 py-2 text-right font-medium" style={{ color: 'var(--text-secondary)' }}>HW $</th>
                    <th className="px-2 py-2 text-right font-medium" style={{ color: 'var(--text-secondary)' }}>Fin $</th>
                    <th className="px-2 py-2 text-right font-medium" style={{ color: 'var(--text-secondary)' }}>Labor $</th>
                    <th className="px-2 py-2 text-right font-medium font-semibold" style={{ color: 'var(--text-secondary)' }}>Cost</th>
                    <th className="px-2 py-2 text-right font-medium font-semibold" style={{ color: '#3498db' }}>Price</th>
                    <th className="px-2 py-2 text-center font-medium w-[160px]" style={{ color: 'var(--text-secondary)' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {lineItemsWithCost.map((item) => {
                    const isBanquette = item.banquette_config && item.banquette_config !== '{}';
                    const isBackCushion = item.back_cushion_config && item.back_cushion_config !== '{}';
                    const isSeatCushion = (item as any).seat_cushion_config && (item as any).seat_cushion_config !== '{}';
                    const isArm = (item as any).arm_config && (item as any).arm_config !== '{}';
                    const isAssembly = item.template_id && !isBanquette && !isBackCushion && !isSeatCushion && !isArm;
                    return (
                      <Fragment key={item.id}>
                      <tr
                        className="border-t transition-colors hover:bg-[var(--bg-hover)]"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        <td className="px-2 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={selectedItemIds.has(item.id)}
                            onChange={() => handleSelectItem(item.id)}
                            className="accent-[var(--accent)]"
                          />
                        </td>
                        <td className="px-3 py-2 font-mono" style={{ color: 'var(--text-muted)' }}>
                          {item.cabinet_code || '-'}
                        </td>
                        <td className="px-3 py-2" style={{ color: 'var(--text-primary)' }}>
                          <div className="flex items-center gap-1.5">
                            {isAssembly && (
                              <span className="px-1 py-0.5 text-[10px] font-semibold rounded" style={{ backgroundColor: 'rgba(46,204,113,0.15)', color: '#2ecc71' }}>ASM</span>
                            )}
                            {isBanquette && (
                              <span className="px-1 py-0.5 text-[10px] font-semibold rounded" style={{ backgroundColor: 'rgba(230,126,34,0.15)', color: '#e67e22' }}>BQ</span>
                            )}
                            <span className="truncate">{item.description}</span>
                            {item._deferred && (
                              <span className="px-1 py-0.5 text-[10px] font-semibold rounded" style={{ backgroundColor: 'rgba(241,196,15,0.15)', color: '#f1c40f' }}>PENDING</span>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-center">
                          <InlineEditableCell value={item.quantity} type="integer" onSave={(v) => handleInlineEdit(item.id, 'quantity', v)} />
                        </td>
                        <td className="px-2 py-2 text-center">
                          <InlineEditableCell value={item.width} type="decimal" onSave={(v) => handleInlineEdit(item.id, 'width', v)} />
                        </td>
                        <td className="px-2 py-2 text-center">
                          <InlineEditableCell value={item.height} type="decimal" onSave={(v) => handleInlineEdit(item.id, 'height', v)} />
                        </td>
                        <td className="px-2 py-2 text-center">
                          <InlineEditableCell value={item.depth} type="decimal" onSave={(v) => handleInlineEdit(item.id, 'depth', v)} />
                        </td>
                        <td className="px-2 py-2 text-center" style={{ color: 'var(--text-secondary)' }}>
                          {item.num_doors}
                        </td>
                        <td className="px-2 py-2 text-center" style={{ color: 'var(--text-secondary)' }}>
                          {item.num_drawers}
                        </td>
                        <td className="px-2 py-2 text-center" style={{ color: 'var(--text-secondary)' }}>
                          {item.num_shelves}
                        </td>
                        <td className="px-2 py-2 text-right font-mono" style={{ color: 'var(--text-secondary)' }}>
                          {item._materialCostSource === 'pending' ? (
                            <Chip size="sm" color="warning" variant="flat" classNames={{ base: 'h-5 px-1', content: 'text-[10px] font-semibold' }}>PENDING</Chip>
                          ) : item._materialCost !== null ? `$${item._materialCost.toFixed(2)}` : '\u2014'}
                        </td>
                        <td className="px-2 py-2 text-right font-mono" style={{ color: 'var(--text-secondary)' }}>
                          {item._hardwareCost !== null ? `$${item._hardwareCost.toFixed(2)}` : '\u2014'}
                        </td>
                        <td className="px-2 py-2 text-right font-mono" style={{ color: 'var(--text-secondary)' }}>
                          {item._finishCost !== null ? `$${item._finishCost.toFixed(2)}` : '\u2014'}
                        </td>
                        <td className="px-2 py-2 text-right font-mono" style={{ color: 'var(--text-secondary)' }}>
                          {(item as any).exclude_labor ? (
                            <button
                              onClick={() => handleToggleItemLabor(item.id, true)}
                              title="Click to include labor"
                              className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded hover:bg-[var(--bg-hover)]"
                              style={{ color: 'var(--text-muted)', textDecoration: 'line-through' }}
                            >
                              excl
                            </button>
                          ) : item._laborCost !== null ? (
                            <button
                              onClick={() => handleToggleItemLabor(item.id, false)}
                              title="Click to exclude labor"
                              className="hover:opacity-70"
                            >
                              ${item._laborCost.toFixed(2)}
                            </button>
                          ) : '\u2014'}
                        </td>
                        <td className="px-2 py-2 text-right font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>
                          {item._deferred || item._materialCostSource === 'pending' ? (
                            <Chip size="sm" color="warning" variant="flat" classNames={{ base: 'h-5 px-1', content: 'text-[10px] font-semibold' }}>PENDING</Chip>
                          ) : item._totalCost !== null ? `$${item._totalCost.toFixed(2)}` : '\u2014'}
                        </td>
                        <td className="px-2 py-2 text-right font-mono font-semibold" style={{ color: '#3498db' }}>
                          {item._deferred || item._materialCostSource === 'pending' ? (
                            <Chip size="sm" color="warning" variant="flat" classNames={{ base: 'h-5 px-1', content: 'text-[10px] font-semibold' }}>PENDING</Chip>
                          ) : item._price !== null ? `$${item._price.toFixed(2)}` : '\u2014'}
                        </td>
                        <td className="px-2 py-2 text-center">
                          <div className="flex items-center justify-center gap-1 flex-nowrap">
                            <button
                              onClick={() => handleOpenLineItem(item.id)}
                              className="p-1 rounded hover:bg-[var(--bg-hover)]"
                              title="Edit"
                            >
                              <Pencil size={13} style={{ color: 'var(--text-muted)' }} />
                            </button>
                            <button
                              onClick={() => setMaterialsPopupItemId(item.id)}
                              className="p-1 rounded hover:bg-[var(--bg-hover)]"
                              title="Materials"
                            >
                              <SwatchBook size={13} style={{ color: 'var(--text-muted)' }} />
                            </button>
                            <button
                              onClick={() => setHardwarePopupItemId(item.id)}
                              className="p-1 rounded hover:bg-[var(--bg-hover)]"
                              title="Hardware"
                            >
                              <Wrench size={13} style={{ color: 'var(--text-muted)' }} />
                            </button>
                            <button
                              onClick={() => setCostBreakdownItemId(item.id)}
                              className="p-1 rounded hover:bg-[var(--bg-hover)]"
                              title="Cost Breakdown"
                            >
                              <DollarSign size={13} style={{ color: 'var(--text-muted)' }} />
                            </button>
                            <button
                              onClick={() => handleDuplicate(item.id)}
                              className="p-1 rounded hover:bg-[var(--bg-hover)]"
                              title="Duplicate"
                            >
                              <Copy size={13} style={{ color: 'var(--text-muted)' }} />
                            </button>
                            <button
                              onClick={() => handleDeleteLineItem(item.id, item.description)}
                              className="p-1 rounded hover:bg-[var(--bg-hover)]"
                              title="Delete"
                            >
                              <Trash2 size={13} style={{ color: 'var(--text-muted)' }} />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {item._fabricDetails.map((fab, idx) => {
                        const unitPrice = fab.yards && fab.yards > 0 ? fab.cost / fab.yards : 0;
                        return (
                          <tr
                            key={`${item.id}-fab-${idx}`}
                            className="border-t"
                            style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-panel)' }}
                          >
                            <td />
                            <td />
                            <td className="px-3 py-1 italic" style={{ color: 'var(--text-muted)', paddingLeft: '2rem' }}>
                              ↳ Fabric: {fab.name}
                            </td>
                            <td className="px-2 py-1 text-center font-mono text-[11px]" style={{ color: 'var(--text-muted)' }}>
                              {fab.yards!.toFixed(2)} yd
                            </td>
                            <td />
                            <td />
                            <td />
                            <td />
                            <td />
                            <td />
                            <td className="px-2 py-1 text-right font-mono text-[11px]" style={{ color: 'var(--text-muted)' }}>
                              ${unitPrice.toFixed(2)}/yd
                            </td>
                            <td />
                            <td />
                            <td />
                            <td className="px-2 py-1 text-right font-mono text-[11px]" style={{ color: 'var(--text-muted)' }}>
                              ${fab.cost.toFixed(2)}
                            </td>
                            <td />
                            <td />
                          </tr>
                        );
                      })}
                      </Fragment>
                    );
                  })}
                  {lineItems.length === 0 && (
                    <tr>
                      <td colSpan={17} className="px-3 py-8 text-center" style={{ color: 'var(--text-muted)' }}>
                        No line items yet. Click "Add Line Item" to get started.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Custom Charges Section */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Custom Charges
                </h3>
                <Button size="sm" variant="light" onPress={handleAddCharge} startContent={<Plus size={13} />}>
                  Add Charge
                </Button>
              </div>
              <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ backgroundColor: 'var(--bg-panel)' }}>
                      <th className="px-3 py-2 text-left font-medium" style={{ color: 'var(--text-secondary)' }}>Description</th>
                      <th className="px-3 py-2 text-center font-medium" style={{ color: 'var(--text-secondary)' }}>Category</th>
                      <th className="px-3 py-2 text-center font-medium" style={{ color: 'var(--text-secondary)' }}>Qty</th>
                      <th className="px-3 py-2 text-right font-medium" style={{ color: 'var(--text-secondary)' }}>Unit Cost</th>
                      <th className="px-3 py-2 text-right font-medium" style={{ color: 'var(--text-secondary)' }}>Total</th>
                      <th className="px-3 py-2 text-right font-medium" style={{ color: 'var(--text-secondary)' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(customCharges as CustomCharge[]).map((charge) => (
                      <tr
                        key={charge.id}
                        className="border-t transition-colors hover:bg-[var(--bg-hover)]"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        <td className="px-3 py-2" style={{ color: 'var(--text-primary)' }}>{charge.description}</td>
                        <td className="px-3 py-2 text-center" style={{ color: 'var(--text-muted)' }}>{charge.category}</td>
                        <td className="px-3 py-2 text-center" style={{ color: 'var(--text-secondary)' }}>{charge.quantity}</td>
                        <td className="px-3 py-2 text-right font-mono" style={{ color: 'var(--text-secondary)' }}>
                          ${charge.unit_cost.toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-medium" style={{ color: 'var(--accent)' }}>
                          ${(charge.quantity * charge.unit_cost).toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => handleDeleteCharge(charge.id)}
                            className="p-1 rounded hover:bg-[var(--bg-hover)]"
                            title="Delete"
                          >
                            <Trash2 size={13} style={{ color: 'var(--text-muted)' }} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {customCharges.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-3 py-4 text-center" style={{ color: 'var(--text-muted)' }}>
                          No custom charges.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Area Cost Totals */}
            {(() => {
              const areaCost = projectCost?.areas.find(a => a.area_id === selectedAreaId);
              if (!areaCost) return null;
              return (
                <div className="rounded-lg border p-4 mb-6" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-panel)' }}>
                  <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Area Cost Summary</h3>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-xs">
                    <div style={{ color: 'var(--text-secondary)' }}>Materials</div>
                    <div className="text-right font-mono" style={{ color: 'var(--text-primary)' }}>${(areaCost.material_cost ?? 0).toFixed(2)}</div>
                    <div style={{ color: 'var(--text-secondary)' }}>Hardware</div>
                    <div className="text-right font-mono" style={{ color: 'var(--text-primary)' }}>${areaCost.hardware_cost.toFixed(2)}</div>
                    <div style={{ color: 'var(--text-secondary)' }}>Finish</div>
                    <div className="text-right font-mono" style={{ color: 'var(--text-primary)' }}>${areaCost.finish_cost.toFixed(2)}</div>
                    <div style={{ color: 'var(--text-secondary)' }}>Labor</div>
                    <div className="text-right font-mono" style={{ color: 'var(--text-primary)' }}>${areaCost.labor_cost.toFixed(2)}</div>
                    <div style={{ color: 'var(--text-secondary)' }}>Custom Charges</div>
                    <div className="text-right font-mono" style={{ color: 'var(--text-primary)' }}>${areaCost.custom_charges_cost.toFixed(2)}</div>
                    <div style={{ color: 'var(--text-secondary)' }}>Toe Kicks</div>
                    <div className="text-right font-mono" style={{ color: 'var(--text-primary)' }}>${areaCost.toe_kick_cost.toFixed(2)}</div>
                    <div className="border-t pt-1 mt-1 font-semibold" style={{ color: 'var(--text-primary)', borderColor: 'var(--border)' }}>Area Total</div>
                    <div className="border-t pt-1 mt-1 text-right font-mono font-semibold" style={{ color: 'var(--accent)', borderColor: 'var(--border)' }}>${areaCost.total_cost.toFixed(2)}</div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Project Cost Summary */}
        {projectCost && <ProjectCostSummary cost={projectCost} />}
      </div>

      <CostBreakdownPanel
        lineItemId={costBreakdownItemId}
        projectCost={projectCost ?? null}
        onClose={() => setCostBreakdownItemId(null)}
      />

      {/* Modals */}
      <AddLineItemDialog
        isOpen={addLineItemDialog.isOpen}
        onClose={addLineItemDialog.onClose}
        onSelectCustom={handleSelectCustom}
        onSelectFromLibrary={handleSelectFromLibrary}
        onSelectBanquette={handleSelectBanquette}
        onSelectBackCushion={handleSelectBackCushion}
        onSelectSeatCushion={handleSelectSeatCushion}
        onSelectArm={handleSelectArm}
        onSelectMetalBase={handleSelectMetalBase}
      />

      {selectedAreaId && (
        <BanquetteBaseModal
          isOpen={banquetteModal.isOpen}
          onClose={() => {
            banquetteModal.onClose();
            setEditingBanquetteId(null);
            setEditingBanquetteConfig(undefined);
            setEditingBanquetteDims(undefined);
          }}
          areaId={selectedAreaId}
          projectId={projectId}
          existingLineItemId={editingBanquetteId ?? undefined}
          existingConfig={editingBanquetteConfig}
          existingDimensions={editingBanquetteDims}
        />
      )}

      {selectedAreaId && (
        <BackCushionModal
          isOpen={backCushionModal.isOpen}
          onClose={() => {
            backCushionModal.onClose();
            setEditingBackCushionId(null);
            setEditingBackCushionConfig(undefined);
            setEditingBackCushionDims(undefined);
          }}
          areaId={selectedAreaId}
          projectId={projectId}
          existingLineItemId={editingBackCushionId ?? undefined}
          existingConfig={editingBackCushionConfig}
          existingDimensions={editingBackCushionDims}
        />
      )}

      {selectedAreaId && (
        <SeatCushionModal
          isOpen={seatCushionModal.isOpen}
          onClose={() => {
            seatCushionModal.onClose();
            setEditingSeatCushionId(null);
            setEditingSeatCushionConfig(undefined);
            setEditingSeatCushionDims(undefined);
          }}
          areaId={selectedAreaId}
          projectId={projectId}
          existingLineItemId={editingSeatCushionId ?? undefined}
          existingConfig={editingSeatCushionConfig}
          existingDimensions={editingSeatCushionDims}
        />
      )}

      {selectedAreaId && (
        <ArmModal
          isOpen={armModal.isOpen}
          onClose={() => {
            armModal.onClose();
            setEditingArmId(null);
            setEditingArmConfig(undefined);
            setEditingArmDims(undefined);
          }}
          areaId={selectedAreaId}
          projectId={projectId}
          existingLineItemId={editingArmId ?? undefined}
          existingConfig={editingArmConfig}
          existingDimensions={editingArmDims}
        />
      )}

      {selectedAreaId && (
        <MetalBaseModal
          isOpen={metalBaseModal.isOpen}
          onClose={() => {
            metalBaseModal.onClose();
            setEditingMetalBaseId(null);
            setEditingMetalBaseConfig(undefined);
          }}
          areaId={selectedAreaId}
          projectId={projectId}
          existingLineItemId={editingMetalBaseId ?? undefined}
          existingConfig={editingMetalBaseConfig}
        />
      )}

      <LineItemModal
        isOpen={lineItemModal.isOpen}
        onClose={lineItemModal.onClose}
        areaId={selectedAreaId!}
        editingId={editingLineItemId}
      />

      <ProjectMaterialsModal
        isOpen={projectMaterialsModal.isOpen}
        onClose={projectMaterialsModal.onClose}
        projectId={projectId}
      />

      {selectedAreaId && (
        <AreaMaterialsModal
          isOpen={areaMaterialsModal.isOpen}
          onClose={areaMaterialsModal.onClose}
          areaId={selectedAreaId}
          projectId={projectId}
        />
      )}

      {selectedAreaId && (
        <ProductPickerModal
          isOpen={productPickerModal.isOpen}
          onClose={productPickerModal.onClose}
          areaId={selectedAreaId}
        />
      )}

      {materialsPopupItemId && selectedAreaId && (
        <MaterialsPopup
          isOpen={!!materialsPopupItemId}
          onClose={() => setMaterialsPopupItemId(null)}
          lineItemId={materialsPopupItemId}
          areaId={selectedAreaId}
          projectId={projectId}
        />
      )}

      {hardwarePopupItemId && (
        <HardwarePopup
          isOpen={!!hardwarePopupItemId}
          onClose={() => setHardwarePopupItemId(null)}
          lineItemId={hardwarePopupItemId}
        />
      )}
    </div>
  );
}
